import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IBlogApiResponse, ICategory, IListing, IState } from "./Article";

// Fetch with retry logic
const fetchWithRetry = async (url: string, retries = 3) => {
  for (let i = 0; i < retries; i++) {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP error: ${res.status}`);
      return await res.json();
    } catch (err) {
      if (i === retries - 1) throw err;
    }
  }
};

// State interface for API data
interface IApiState {
  categoryData: ICategory | null;
  stateData: IState | null;
  listingData: IListing | null;
  blogs: IBlogApiResponse | null;
  blogMeta: {
    total: number;
    limit: number;
    page: number;
    lastPage: number;
  } | null;
  isLoading: boolean;
  error: string | null;
  hasFetched: {
    blogs: boolean;
    category: boolean;
    listing: boolean;
    states: boolean;
  };
}

// Initial state for the API slice
const initialState: IApiState = {
  categoryData: null,
  stateData: null,
  listingData: null,
  blogs: null,
  blogMeta: null,
  isLoading: false,
  error: null,
  hasFetched: {
    blogs: false,
    category: false,
    listing: false,
    states: false,
  },
};

// Async thunks for fetching data
export const fetchCategory = createAsyncThunk(
  "api/fetchCategory",
  async (_, { getState, rejectWithValue }) => {
    const { hasFetched } = (getState() as any).api;
    if (hasFetched.category) return null; // Skip if already fetched
    try {
      return await fetchWithRetry(
        "https://backend.onedialusa.com/api/categories"
      );
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchStates = createAsyncThunk(
  "api/fetchStates",
  async (_, { getState, rejectWithValue }) => {
    const { hasFetched } = (getState() as any).api;
    if (hasFetched.states) return null; // Skip if already fetched
    try {
      return await fetchWithRetry("https://backend.onedialusa.com/api/states");
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchListing = createAsyncThunk(
  "api/fetchListing",
  async (_, { getState, rejectWithValue }) => {
    const { hasFetched } = (getState() as any).api;
    if (hasFetched.listing) return null; // Skip if already fetched
    try {
      return await fetchWithRetry(
        "https://backend.onedialusa.com/api/listings"
      );
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchBlogs = createAsyncThunk(
  "api/fetchBlogs",
  async (
    { page = 1, limit = 6 }: { page?: number; limit?: number } = {},
    { getState, rejectWithValue }
  ) => {
    try {
      const url = `https://backend.onedialusa.com/api/blogs?page=${page}&limit=${limit}`;
      return await fetchWithRetry(url);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const apiSlice = createSlice({
  name: "api",
  initialState,
  reducers: {
    resetState: () => initialState, // Reset state if needed
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCategory.fulfilled, (state, action) => {
        if (action.payload) {
          state.categoryData = action.payload;
          state.hasFetched.category = true; // Mark as fetched
        }
      })
      .addCase(fetchStates.fulfilled, (state, action) => {
        if (action.payload) {
          state.stateData = action.payload;
          state.hasFetched.states = true; // Mark as fetched
        }
      })
      .addCase(fetchListing.fulfilled, (state, action) => {
        if (action.payload) {
          state.listingData = action.payload;
          state.hasFetched.listing = true; // Mark as fetched
        }
      })
      .addCase(fetchBlogs.fulfilled, (state, action) => {
        if (action.payload) {
          state.blogs = action.payload;
          const { total = 0, limit = 6, page = 1 } = action.payload;
          const lastPage = Math.ceil(total / limit);
          state.blogMeta = { total, limit, page, lastPage };
        }
      })
      .addCase(fetchBlogs.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const { resetState } = apiSlice.actions;
export default apiSlice.reducer;
